import { useCallback } from 'react'
import { AILessonPlanResponse, AIMeeting, AIActivity } from '../service/aiLessonPlanService'
import {
  extractMeetingTitle,
  extractMeetingDate,
  getDefaultMeetingTime,
  getDefaultMeetingDuration,
  extractActivityTitle,
  extractActivityPlanning,
  extractActivityItems,
  validateAIResponse,
} from '../utils/aiResponseProcessors'
import { ActivityLv4ItemAttribute } from '../utils/activityItemsFormatter'

export interface ProcessedMeeting {
  title: string
  date: string
  time: string
  duration: number
  activities: ProcessedActivity[]
}

export interface ProcessedActivity {
  title: string
  planning: string
  activity_lv4_items_attributes: ActivityLv4ItemAttribute[]
}

/**
 * Hook para processar a resposta da IA de forma estruturada
 */
export function useAILessonProcessor() {
  /**
   * Processa uma atividade individual
   */
  const processActivity = useCallback((activity: AIActivity): ProcessedActivity => {
    const items = extractActivityItems(activity)

    return {
      title: extractActivityTitle(activity),
      planning: extractActivityPlanning(activity),
      activity_lv4_items_attributes: items,
    }
  }, [])

  /**
   * Processa um meeting individual
   */
  const processMeeting = useCallback((meeting: AIMeeting): ProcessedMeeting => {
    return {
      title: extractMeetingTitle(meeting),
      date: extractMeetingDate(meeting),
      time: getDefaultMeetingTime(),
      duration: getDefaultMeetingDuration(),
      activities: meeting.activities.map(activity => processActivity(activity)),
    }
  }, [processActivity])

  /**
   * Processa a resposta completa da IA
   */
  const processAIResponse = useCallback((
    response: AILessonPlanResponse | null
  ): ProcessedMeeting[] | null => {
    if (!validateAIResponse(response)) {
      console.warn('Resposta da IA inválida')
      return null
    }

    return response!.meetings.map(meeting => processMeeting(meeting))
  }, [processMeeting])

  return {
    processActivity,
    processMeeting,
    processAIResponse,
  }
}
